import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import SportTypeSelector from '../components/SportTypeSelector';
import { getApiBaseUrl } from '../apiBase';

const API = getApiBaseUrl();

export default function ChangeAccountTypePage() {
    const { t } = useTranslation(['registerPage', 'auth', 'billing', 'common']);
    const token = localStorage.getItem('token');

    const ROLES = useMemo(() => [
        { value: 'Owner', icon: '🏫', label: t('registerPage:roleOwner'), desc: t('registerPage:roleOwnerDesc'), billing: true },
        { value: 'Coach', icon: '🎽', label: t('registerPage:roleCoach'), desc: t('registerPage:roleCoachDesc'), billing: false },
        { value: 'FreelanceCoach', icon: '👤', label: t('registerPage:roleFreelance'), desc: t('registerPage:roleFreelanceDesc'), billing: true },
        { value: 'Member', icon: '⚽', label: t('registerPage:roleMember'), desc: t('registerPage:roleMemberDesc'), billing: false },
        { value: 'Parent', icon: '👨‍👩‍👧', label: t('auth:accountType.parent'), desc: t('registerPage:roleMemberDesc'), billing: false }
    ], [t]);

    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [canAdd, setCanAdd] = useState(true);
    const [blockReason, setBlockReason] = useState('');
    const [existingProfiles, setExistingProfiles] = useState([]);
    const [role, setRole] = useState('Member');
    const [sportTypeId, setSportTypeId] = useState('');
    const [sportTypeIds, setSportTypeIds] = useState([]);
    const [sportLabel, setSportLabel] = useState('');
    const [city, setCity] = useState('');
    const [billingModal, setBillingModal] = useState(null);
    const [billingConfirmed, setBillingConfirmed] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    const isFreelance = role === 'FreelanceCoach';
    const isOwner = role === 'Owner';
    const isBillingRole = role === 'Owner' || role === 'FreelanceCoach';

    useEffect(() => {
        if (!token) return;
        axios.get(`${API}/auth/account-type-status`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then((res) => {
            const can = res.data.canAddProfile ?? res.data.canChange;
            setCanAdd(!!can);
            setBlockReason(res.data.blockReason || '');
            setExistingProfiles(res.data.profiles || []);
        }).catch(() => {
            setCanAdd(false);
            setBlockReason(t('auth:accountType.loadError'));
        }).finally(() => setLoading(false));
    }, [token, t]);

    function buildPayload(confirmed = false) {
        const payload = {
            role,
            sportTypeId: sportTypeId ? parseInt(sportTypeId, 10) : null,
            billingConfirmed: confirmed
        };
        if (isFreelance) {
            payload.sportTypeIds = sportTypeIds.map((id) => parseInt(id, 10));
            payload.city = city.trim();
        }
        return payload;
    }

    async function submitProfile(confirmed = false) {
        setBusy(true);
        setError('');
        try {
            const res = await axios.post(
                `${API}/auth/change-account-type`,
                buildPayload(confirmed),
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (res.data.requiresConfirmation) {
                setBillingModal(res.data.pricing || {});
                setBillingConfirmed(false);
                setBusy(false);
                return;
            }

            setSuccess(res.data.message || t('auth:profileAdded'));
            setBillingModal(null);
        } catch (err) {
            const d = err.response?.data;
            setError(d?.message || d?.error || t('common:error'));
        }
        setBusy(false);
    }

    async function onSave(e) {
        e.preventDefault();
        if (isOwner && !sportTypeId) {
            setError(t('registerPage:errSportRequired'));
            return;
        }
        if (isFreelance) {
            if (sportTypeIds.length === 0) {
                setError(t('registerPage:errSportMulti'));
                return;
            }
            if (!city.trim()) {
                setError(t('registerPage:errCity'));
                return;
            }
        }
        await submitProfile(false);
    }

    async function confirmBilling() {
        if (!billingConfirmed) return;
        await submitProfile(true);
    }

    if (loading) {
        return (
            <OwnerPageLayout title={t('auth:accountType.title')} maxWidth={640}>
                <p>{t('common:loading')}</p>
            </OwnerPageLayout>
        );
    }

    if (!canAdd) {
        return (
            <OwnerPageLayout title={t('auth:accountType.cannotChange')} maxWidth={640}>
                <p style={{ color: 'var(--sp-gray-600)' }}>{blockReason}</p>
                <Link to="/profile" className="sp-btn sp-btn--outline" style={{ marginTop: 16, display: 'inline-block' }}>
                    ← {t('nav:profile')}
                </Link>
            </OwnerPageLayout>
        );
    }

    return (
        <OwnerPageLayout
            title={t('auth:addProfile')}
            subtitle={t('auth:accountType.subtitle')}
            maxWidth={640}
        >
            {existingProfiles.length > 0 ? (
                <div className="sp-panel" style={{ marginBottom: 20, fontSize: 14 }}>
                    <strong>{t('auth:switchProfile')}</strong>
                    <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                        {existingProfiles.map((p) => (
                            <li key={p.id}>
                                {p.icon} {p.schoolName || p.childName || p.type}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}

            {success ? (
                <div style={{
                    background: '#e8f5e9',
                    color: '#2e7d32',
                    padding: '14px 16px',
                    borderRadius: 10,
                    marginBottom: 20
                }}>
                    {success}
                    <div style={{ marginTop: 12 }}>
                        <Link to="/profile" className="sp-btn sp-btn--primary">{t('nav:profile')}</Link>
                    </div>
                </div>
            ) : (
                <form onSubmit={onSave}>
                    {ROLES.map((item) => (
                        <button
                            key={item.value}
                            type="button"
                            onClick={() => setRole(item.value)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                width: '100%',
                                textAlign: 'left',
                                padding: 14,
                                marginBottom: 10,
                                borderRadius: 12,
                                border: `2px solid ${role === item.value ? 'var(--sp-primary)' : '#e2e8f0'}`,
                                background: role === item.value ? '#fff5f5' : '#fff',
                                cursor: 'pointer'
                            }}
                        >
                            <span style={{ fontSize: 26 }}>{item.icon}</span>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 700 }}>{item.label}</div>
                                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{item.desc}</div>
                                {item.billing ? (
                                    <div style={{ fontSize: 11, color: '#EB3333', marginTop: 4, fontWeight: 600 }}>
                                        {t(`auth:accountTypeBilling.${item.value.toLowerCase()}`, {
                                            defaultValue: t('billing:paidPlan')
                                        })}
                                    </div>
                                ) : null}
                            </div>
                            {role === item.value ? <span style={{ color: 'var(--sp-primary)', fontWeight: 800 }}>✓</span> : null}
                        </button>
                    ))}

                    {isOwner ? (
                        <div style={{ marginBottom: 16 }}>
                            <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                                {t('registerPage:sportSchool')}
                            </label>
                            <SportTypeSelector
                                value={sportTypeId}
                                onChange={(id, sport) => {
                                    setSportTypeId(String(id));
                                    setSportLabel(sport ? `${sport.emoji || ''} ${sport.name}`.trim() : '');
                                }}
                            />
                            {sportLabel ? <p style={{ fontSize: 13, color: 'var(--sp-primary)' }}>{sportLabel}</p> : null}
                        </div>
                    ) : null}

                    {isFreelance ? (
                        <>
                            <div style={{ marginBottom: 16 }}>
                                <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                                    {t('registerPage:sportsMulti')}
                                </label>
                                <SportTypeSelector
                                    multiple
                                    values={sportTypeIds}
                                    onChange={(nextIds) => setSportTypeIds(nextIds)}
                                />
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
                                    {t('registerPage:city')}
                                </label>
                                <input
                                    className="sp-input"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    placeholder={t('registerPage:cityPlaceholder')}
                                />
                            </div>
                        </>
                    ) : null}

                    {isBillingRole ? (
                        <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
                            {t('auth:billingWarning')}
                        </p>
                    ) : null}

                    {error ? (
                        <div style={{ background: '#fce4ec', color: '#c62828', padding: 10, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                            {error}
                        </div>
                    ) : null}

                    <button type="submit" className="sp-btn sp-btn--primary" disabled={busy} style={{ width: '100%' }}>
                        {busy ? t('common:loading') : t('auth:addProfile')}
                    </button>
                </form>
            )}

            {billingModal ? (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 3000,
                    padding: 20
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: 16,
                        padding: 28,
                        maxWidth: 440,
                        width: '100%'
                    }}>
                        <h3 style={{ margin: '0 0 12px' }}>{t('billing:paidPlan')}</h3>
                        <p style={{ color: '#64748b', lineHeight: 1.5, marginBottom: 16 }}>
                            {role === 'Owner'
                                ? (billingModal.ownerWarning || t('billing:ownerWarning'))
                                : (billingModal.freelanceWarning || t('billing:freelanceWarning'))}
                        </p>
                        {billingModal.plans?.length ? (
                            <ul style={{ fontSize: 13, marginBottom: 16, paddingLeft: 20 }}>
                                {billingModal.plans.map((plan) => (
                                    <li key={plan.name}>
                                        {plan.displayName || plan.name} — {plan.monthlyPrice} ₽/мес
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 20, cursor: 'pointer' }}>
                            <input
                                type="checkbox"
                                checked={billingConfirmed}
                                onChange={(e) => setBillingConfirmed(e.target.checked)}
                            />
                            <span>{t('billing:confirmBilling')}</span>
                        </label>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button
                                type="button"
                                className="sp-btn sp-btn--primary"
                                disabled={!billingConfirmed || busy}
                                onClick={confirmBilling}
                                style={{ flex: 1 }}
                            >
                                {t('auth:addProfile')}
                            </button>
                            <button
                                type="button"
                                className="sp-btn sp-btn--outline"
                                onClick={() => setBillingModal(null)}
                            >
                                {t('common:cancel')}
                            </button>
                        </div>
                        <Link to="/pricing" style={{ display: 'block', marginTop: 12, fontSize: 13, color: 'var(--sp-primary)' }}>
                            {t('billing:seePricing')}
                        </Link>
                    </div>
                </div>
            ) : null}
        </OwnerPageLayout>
    );
}
